import {
    PROFILE_TAB_OVERVIEW_FETCH_REQUEST,
    PROFILE_TAB_OVERVIEW_FETCH_SUCCESS,
    PROFILE_TAB_OVERVIEW_FETCH_FAILURE,
    PROFILE_TAB_RESULTS_FETCH_REQUEST,
    PROFILE_TAB_RESULTS_FETCH_SUCCESS,
    PROFILE_TAB_RESULTS_FETCH_FAILURE,
    PROFILE_TAB_EVENTS_FETCH_REQUEST,
    PROFILE_TAB_EVENTS_FETCH_SUCCESS,
    PROFILE_TAB_EVENTS_FETCH_FAILURE,
    PROFILE_TAB_CLUBS_FETCH_REQUEST,
    PROFILE_TAB_CLUBS_FETCH_SUCCESS,
    PROFILE_TAB_CLUBS_FETCH_FAILURE,
    PROFILE_TAB_STATS_FETCH_REQUEST,
    PROFILE_TAB_STATS_FETCH_SUCCESS,
    PROFILE_TAB_STATS_FETCH_FAILURE,
    PROFILE_TAB_CLEAR_ALL_DATA,
} from './actionTypes/profile-tab-action-types';
import { ClubTierTypes } from '../utils/clubs';
import { ComparatorTypes, comparator } from '../utils/comparator';
import { EventScheduleTypes } from '../utils/eventConstants';
import { PlayerProfileTabIdentifiers } from '../utils/constants';
import { getCombinedProfileEvents } from '../utils/events';
import { getPlayerOverview, getPlayerResults, getPlayerEvents, getPlayerClubs, getPlayerStats } from '../utils/profilePage';
import { showErrorPopup } from './notification-actions';

const { OVERVIEW, RESULTS, EVENTS, CLUBS, STATS } = PlayerProfileTabIdentifiers;

export const getOverviewData = () => {
    return async (dispatch, getState, { mainApi }) => {
        const { playerId } = getState().profile;

        dispatch({ type: PROFILE_TAB_OVERVIEW_FETCH_REQUEST });

        try {
            const response = await getPlayerOverview(mainApi, playerId);
            if (response) {
                const { data } = response;
                const { recentEvents, upcomingEvents } = data || {};
                let combinedRecentEvents = recentEvents;
                let combinedUpcomingEvents = upcomingEvents;

                if (recentEvents) {
                    combinedRecentEvents = getCombinedProfileEvents(EventScheduleTypes.RECENT, recentEvents);
                }

                if (upcomingEvents) {
                    combinedUpcomingEvents = getCombinedProfileEvents(EventScheduleTypes.UPCOMING, upcomingEvents);
                }

                dispatch({ type: PROFILE_TAB_OVERVIEW_FETCH_SUCCESS, payload: { ...data, recentEvents: combinedRecentEvents, upcomingEvents: combinedUpcomingEvents } });
            }
        } catch (error) {
            dispatch({ type: PROFILE_TAB_OVERVIEW_FETCH_FAILURE, payload: error });
            dispatch(showErrorPopup({
                message: 'Error fetching player overview.',
                error,
            }));
        }
    };
};

export const getResultsData = () => {
    return async (dispatch, getState, { mainApi }) => {
        const { playerId, gameType, year } = getState().profile;

        dispatch({ type: PROFILE_TAB_RESULTS_FETCH_REQUEST });

        try {
            const response = await getPlayerResults(mainApi, { gameType, year }, playerId);
            if (response) {
                const { data } = response;
                dispatch({ type: PROFILE_TAB_RESULTS_FETCH_SUCCESS, payload: data });
            }
        } catch (error) {
            dispatch({ type: PROFILE_TAB_RESULTS_FETCH_FAILURE, payload: error });
            dispatch(showErrorPopup({
                message: 'Error fetching player results.',
                error,
            }));
        }
    };
};

export const getEventsData = () => {
    return async (dispatch, getState, { mainApi }) => {
        const { playerId, eventScheduleType } = getState().profile;

        dispatch({ type: PROFILE_TAB_EVENTS_FETCH_REQUEST });

        try {
            const response = await getPlayerEvents(mainApi, eventScheduleType, playerId);
            if (response) {
                const { data } = response;
                const combinedEvents = getCombinedProfileEvents(eventScheduleType, data);
                dispatch({ type: PROFILE_TAB_EVENTS_FETCH_SUCCESS, payload: combinedEvents });
            }
        } catch (error) {
            dispatch({ type: PROFILE_TAB_EVENTS_FETCH_FAILURE, payload: error });
            dispatch(showErrorPopup({
                message: 'Error fetching player events.',
                error,
            }));
        }
    };
};

export const getClubsData = () => {
    return async (dispatch, getState, { mainApi }) => {
        const { playerId } = getState().profile;

        dispatch({ type: PROFILE_TAB_CLUBS_FETCH_REQUEST });

        try {
            const response = await getPlayerClubs(mainApi, playerId);
            if (response) {
                const { data } = response;
                let sortedClubs = data;
                let sortedRecentClubs;

                const recentClubComparatorParams = [{ type: ComparatorTypes.NUMBER, field: 'memberCount', descend: true }];
                const clubComparatorParams = [{ type: ComparatorTypes.STRING, field: 'name' }];

                if (data) {
                    const filteredClubs = data.filter(club => club.tierTypeId !== ClubTierTypes.CREATE_PLAY);
                    sortedClubs = filteredClubs.sort(comparator(clubComparatorParams));
                    sortedRecentClubs = filteredClubs.sort(comparator(recentClubComparatorParams)).slice(0, 2);
                }

                const clubData = { clubs: sortedClubs, recentClubs: sortedRecentClubs };

                dispatch({ type: PROFILE_TAB_CLUBS_FETCH_SUCCESS, payload: clubData });
            }
        } catch (error) {
            dispatch({ type: PROFILE_TAB_CLUBS_FETCH_FAILURE, payload: error });
            dispatch(showErrorPopup({
                message: 'Error fetching player clubs.',
                error,
            }));
        }
    };
};

export const getStatsData = () => {
    return async (dispatch, getState, { mainApi }) => {
        const { playerId, statsState } = getState().profile;

        dispatch({ type: PROFILE_TAB_STATS_FETCH_REQUEST });

        try {
            const response = await getPlayerStats(mainApi, statsState, playerId);
            if (response) {
                const { data } = response;
                dispatch({ type: PROFILE_TAB_STATS_FETCH_SUCCESS, payload: data });
            }
        } catch (error) {
            dispatch({ type: PROFILE_TAB_STATS_FETCH_FAILURE, payload: error });
            dispatch(showErrorPopup({
                message: 'Error fetching player stats.',
                error,
            }));
        }
    };
};

export const clearProfileTabData = () => (dispatch) => {
    dispatch({ type: PROFILE_TAB_CLEAR_ALL_DATA });
};

export const changeGameType = (gameType) => {
    return (dispatch) => {
        dispatch({ type: 'PROFILE:CHANGE_GAME_TYPE', gameType });
        dispatch(getResultsData());
    };
};
export const changeYear = (year) => {
    return (dispatch) => {
        dispatch({ type: 'PROFILE:CHANGE_YEAR', year });
        dispatch(getResultsData());
    };
};

export const changeEventScheduleType = (scheduleType) => {
    return (dispatch) => {
        dispatch({ type: 'PROFILE:CHANGE_EVENT_SCHEDULE_TYPE', scheduleType });
        dispatch(getEventsData());
    };
};

export const updateStatsParams = (params) => {
    return (dispatch, getState) => {
        const statsState = {
            ...(getState().profile.statsState),
            ...(params),
        };
        dispatch({
            type: 'PROFILE:UPDATE_STATS_PARAMS',
            data: { params: statsState },
        });
        dispatch(getStatsData());
    };
};

export const getDataForTab = (tab) => {
    return (dispatch) => {
        switch (tab) {
            case OVERVIEW:
                dispatch(getOverviewData());
                break;
            case RESULTS:
                dispatch(getResultsData());
                break;
            case EVENTS:
                dispatch(getEventsData());
                break;
            case CLUBS:
                dispatch(getClubsData());
                break;
            case STATS:
                dispatch(getStatsData());
                break;
            default:
                break;
        }
    };
};
